import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, TouchedChangeEvent, ValidatorFn, Validators } from '@angular/forms';
import { KeyValueListType, KeyValueType } from '@models/common';
import { FormFieldValueType } from '@models/module/fields/main';
import { deepComputed } from '@ngrx/signals';
import {
  CipoControl,
  CipoFieldConfig,
  CipoFieldTypes,
  DataSourceInfoModel,
  FieldsService,
} from '@shared/components/fields/common';
import { filter, map, of, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'cipo-data-list',
  template: '',
})
export abstract class CipoControlBase<TValue = FormFieldValueType, TConfig = CipoFieldConfig> {
  protected fieldsService = inject(FieldsService);
  protected destroyRef = inject(DestroyRef);

  /** This holds the configuration for the field.
   *
   * This must be at least an empty object at creation
   *
   * If not, the fieldData will be get from control.fieldData
   */
  fieldData = model<TConfig>(null);

  /** @deprecated */
  separateOptions = model<KeyValueListType<any>[]>(null, { alias: 'options' });

  /** This can be a simple formControl if the fieldData is provided separately
   *
   * If you don't provide the fieldData separately, the control must be a CipoControl with fieldData inside.
   */
  /* Not required by default, because cipo-attachment can be used without providing a control */
  control = input<CipoControl<TValue, TConfig>>();

  onblur = output<void>();

  /** This is a deep computed of fieldData, to be used inside the cipo-field component.
   *
   * If you need more fieldData properties, add them here.
   */
  deepFieldData = deepComputed(() => {
    const fieldData = this.fieldData() as CipoFieldConfig;
    /* we cannot return { ...fieldData } because if some prop is not defined (ex: fieldData.required) 
    - calling it with deepFieldData.required() will throw an error (calling undefined) */
    return {
      min: fieldData?.min,
      max: fieldData?.max,
      minDate: fieldData?.minDate,
      maxDate: fieldData?.maxDate,
      options: fieldData?.options,
      multiple: fieldData?.multiple,
      readonly: fieldData?.readonly,
      required: fieldData?.required,
      maxLength: fieldData?.maxLength,
      currency: fieldData?.currency,
      position: fieldData?.position,
      showPosition: fieldData?.showPosition,
    };
  });

  maxHeight = computed(() => {
    const rows = this.deepFieldData.position()?.rows;
    /* One grid-row height should allow 3 row of elements to be visible (130px) */
    return rows ? `${rows * 100}px` : '100%';
  });

  initialControlValue = computed(() => this.control()?.value);
  valueChanged = computed(() => this.formValue() !== this.initialControlValue());
  formValue = toSignal(
    toObservable(this.control).pipe(
      switchMap(ctrl => (ctrl ? ctrl.valueChanges.pipe(startWith(ctrl.value)) : of(null))),
    ),
  );
  /** signal that reflects whether the control has been touched. Useful for propagation of touched state for internal Controls (ex: CipoDateTime) */
  controlTouched = toSignal(
    toObservable(this.control).pipe(
      switchMap(ctrl =>
        ctrl
          ? ctrl.events.pipe(
              filter(event => event instanceof TouchedChangeEvent),
              map(event => event.touched),
            )
          : of(false),
      ),
    ),
  );

  canClearInput = computed(() => {
    const { readonly, preventClearInput, suffixIcon } = this.fieldData() as CipoFieldConfig;
    return !readonly && !preventClearInput && !suffixIcon;
  });

  validators = new Map<string, ValidatorFn>();
  /* custom validators added to the control. 
  May be a problem when the validators are added on a control, and then removed. */
  customValidator: ValidatorFn;
  /** Holds the value of the input when it receives focus, used to detect changes on blur */
  valueOnFocus: string;
  private readonly lastLoadedDataSourceOptions = signal<KeyValueType[] | null>(null);

  constructor() {
    /* If the fieldData is not provided at the creation -> set it from the control */
    effect(() => {
      const fieldData = untracked(() => this.fieldData());
      const control = this.control();
      this.customValidator = control?.validator;
      if (control && !fieldData) {
        this.fieldData.set(control.fieldData);
      }
    });

    effect(() => {
      const options = this.separateOptions();
      if (options) {
        this.fieldData.update(fd => ({ ...fd, options }));
      }
    });

    effect(() => this.setRequiredValidator(this.deepFieldData.required()));
    effect(() => this.setMaxLengthValidator(this.deepFieldData.maxLength()));

    effect(() => {
      const options = this.lastLoadedDataSourceOptions();
      const readonly = this.deepFieldData.readonly();
      if (options && !readonly) {
        untracked(() => this.clearValuesMissingFromDataSource(options));
      }
    });
  }

  /** click on 'X' icon at the end of the input */
  clearValue() {
    this.control().setValue(null);
    this.control().markAsTouched();
    this.control().markAsDirty();
    this.onblur.emit();
  }

  /** By default it's updating the input control. You can provide another control for cipo-field with custom controls */
  updateValidators(control?: FormControl<any>) {
    const ctrl = control || this.control();
    if (!ctrl) {
      return;
    }
    /* Add validators computed from fieldData and append customValidator (added initialy by user on control) */
    ctrl.setValidators(
      Array.from(this.validators.values())
        .concat(this.customValidator)
        .filter(v => v),
    );
    /* Added emitEvent: false to avoid emitting valueChanges after validators change.  */
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private setRequiredValidator(required: boolean) {
    if (required) {
      this.validators.set('required', Validators.required);
    } else {
      this.validators.delete('required');
    }
    this.updateValidators();
  }

  setMaxLengthValidator(maxLength: number | null) {
    if (maxLength) {
      this.validators.set('maxLength', Validators.maxLength(maxLength));
    } else {
      this.validators.delete('maxLength');
    }
    this.updateValidators();
  }

  loadDataSource(dataSourceInfo?: DataSourceInfoModel) {
    this.fieldsService.loadDataSource(
      this.fieldData,
      this.formValue() as FormFieldValueType,
      this.destroyRef,
      dataSourceInfo,
      options => {
        this.lastLoadedDataSourceOptions.set(options);
      },
    );
  }

  protected checkOptions() {
    this.fieldsService.checkOptions(
      this.fieldData,
      this.formValue() as FormFieldValueType,
      this.destroyRef,
      options => {
        this.lastLoadedDataSourceOptions.set(options);
      },
    );
  }

  private clearValuesMissingFromDataSource(options: KeyValueType[]) {
    const { allowNewValues, multiple, type } = this.fieldData() as CipoFieldConfig;
    if (allowNewValues || ![CipoFieldTypes.Select, CipoFieldTypes.Listbox, CipoFieldTypes.RadioGroup].includes(type)) {
      return;
    }

    const control = this.control();
    const value = control?.value;
    const selectedValues = Array.isArray(value) ? value : value == null ? [] : [value];
    const availableKeys = new Set(options.map(option => String(option.key)));
    const validValues = selectedValues.filter(selectedValue => availableKeys.has(String(selectedValue)));

    if (validValues.length === selectedValues.length) {
      return;
    }

    control.setValue((multiple ? validValues : null) as TValue);
  }
}
